import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, RefreshCw, Users, AlertTriangle, Edit, Trash2, CheckSquare, Square, X,
  User, UserPlus, Minus, Plus, Save, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { NoteIconButton } from './NotesModal';
import api, { jobsAPI, bookingsAPI } from '../../services/api';
import { toast } from 'sonner';

const JobBoardTab = ({
  jobBoard,
  jobBoardFilter,
  setJobBoardFilter,
  showWorkerPreview,
  setShowWorkerPreview,
  allWorkersHours,
  workers,
  noteCounts,
  setNotesModal,
  handlePostAllJobs,
  handlePostJob,
  handleApproveJob,
  handleDenyJob,
  handleManualAssign,
  handleViewWorkerProfile,
  setEditingJob,
  setShowJobEditModal,
  handleDeleteJob,
  fetchAllData,
  currentUser // Add current user prop for owner self-assignment
}) => {
  // Multi-select state
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  
  // Workers needed edit state
  const [editingWorkersNeeded, setEditingWorkersNeeded] = useState(null);
  const [workersNeededValue, setWorkersNeededValue] = useState(1);
  const [savingWorkersNeeded, setSavingWorkersNeeded] = useState(false);
  
  // Assign to me loading state
  const [assigningToMe, setAssigningToMe] = useState(null);
  
  // Handle updating workers needed
  const handleUpdateWorkersNeeded = async (jobId) => {
    if (workersNeededValue < 1 || workersNeededValue > 10) {
      toast.error('Workers needed must be between 1 and 10');
      return;
    }
    
    try {
      setSavingWorkersNeeded(true);
      // Use booking endpoint since jobs are bookings
      await api.put(`/bookings/${jobId}/workers-needed?workers_needed=${workersNeededValue}`);
      toast.success(`Updated to ${workersNeededValue} worker(s) needed`);
      setEditingWorkersNeeded(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workers needed');
    } finally {
      setSavingWorkersNeeded(false);
    }
  };
  
  // Handle owner assigning themselves to a job
  const handleAssignToMe = async (job) => {
    if (!currentUser?.id) {
      toast.error('Unable to identify current user');
      return;
    }
    
    try {
      setAssigningToMe(job.id);
      // Try to assign using booking endpoint
      await api.post(`/bookings/${job.id}/assign?worker_id=${currentUser.id}`);
      toast.success(`You've been assigned to ${job.service_name || 'the job'}`);
      fetchAllData();
    } catch (error) {
      // If booking endpoint fails, try job endpoint
      try {
        await jobsAPI.manualAssign(job.id, currentUser.id);
        toast.success(`You've been assigned to ${job.service_name || 'the job'}`);
        fetchAllData();
      } catch (e2) {
        toast.error(e2.response?.data?.detail || 'Failed to assign');
      }
    } finally {
      setAssigningToMe(null);
    }
  };
  
  // Check if job needs multiple workers
  const isServiceJob = (job) => {
    return job.service_type === 'rides' || job.service_name?.toLowerCase().includes('ride');
  };
  
  const handleBulkDelete = async () => {
    if (selectedJobs.length === 0) return;
    if (!window.confirm(`Delete ${selectedJobs.length} job(s)? This will move them to archive.`)) return;
    
    try {
      const result = await jobsAPI.bulkDeleteJobs(selectedJobs, 'Bulk delete by owner');
      toast.success(`${result.data.deleted_count} job(s) deleted`);
      if (result.data.skipped?.length > 0) {
        toast.info(`${result.data.skipped.length} job(s) skipped (in progress or completed)`);
      }
      setSelectedJobs([]);
      setSelectMode(false);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to delete some jobs');
    }
  };
  
  const toggleSelectJob = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
  };
  
  const selectAllJobs = () => {
    const selectableJobs = jobBoard.jobs
      ?.filter(job => !['in_progress', 'completed'].includes(job.status))
      .filter(job => jobBoardFilter === 'all' || job.status === jobBoardFilter)
      .map(job => job.id) || [];
    setSelectedJobs(selectableJobs);
  };
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="job-board-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Job Board & Assignments</h1>
        <div className="flex gap-2">
          {selectMode && selectedJobs.length > 0 && (
            <>
              <Button onClick={handleBulkDelete} variant="destructive" size="sm">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete ({selectedJobs.length})
              </Button>
              <Button onClick={() => { setSelectedJobs([]); setSelectMode(false); }} variant="ghost" size="sm">
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
          {!selectMode && !showWorkerPreview && jobBoard.jobs?.length > 0 && (
            <Button onClick={() => setSelectMode(true)} variant="outline" size="sm">
              <CheckSquare className="w-4 h-4 mr-2" />
              Select Multiple
            </Button>
          )}
          <Button 
            onClick={() => setShowWorkerPreview(!showWorkerPreview)} 
            variant={showWorkerPreview ? "default" : "outline"} 
            size="sm"
            data-testid="worker-preview-toggle"
          >
            <Eye className="w-4 h-4 mr-2" />
            {showWorkerPreview ? 'Exit Preview' : 'Preview as Worker'}
          </Button>
          <Button onClick={handlePostAllJobs} variant="outline" size="sm" data-testid="post-all-jobs">
            Post All Pending
          </Button>
          <Button onClick={fetchAllData} variant="outline" size="sm" data-testid="refresh-jobs">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Select All in select mode */}
      {selectMode && !showWorkerPreview && (
        <div className="mb-4 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={selectAllJobs}>
            Select All Deletable
          </Button>
          <span className="text-sm text-[var(--text-muted)]">
            {selectedJobs.length} selected
          </span>
        </div>
      )}

      {/* Worker Preview Mode Banner */}
      {showWorkerPreview && (
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Eye className="w-5 h-5 text-[#D4AF37]" />
          <div>
            <p className="font-semibold text-blue-800">Worker Preview Mode</p>
            <p className="text-sm text-[#D4AF37]">You are viewing the job board as a worker would see it. Only available jobs are shown.</p>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      {jobBoard.stats && (
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-6">
          <div 
            className={`card p-3 text-center cursor-pointer transition-all ${jobBoardFilter === 'all' ? 'ring-2 ring-[var(--primary)]' : ''}`}
            onClick={() => setJobBoardFilter('all')}
            data-testid="filter-all"
          >
            <p className="text-2xl font-bold">{jobBoard.stats.total || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Total</p>
          </div>
          <div 
            className={`card p-3 text-center cursor-pointer transition-all bg-[var(--background-subtle)] ${jobBoardFilter === 'pending' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => setJobBoardFilter('pending')}
            data-testid="filter-pending"
          >
            <p className="text-2xl font-bold text-[var(--text-secondary)]">{jobBoard.stats.pending || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Not Posted</p>
          </div>
          <div 
            className={`card p-3 text-center cursor-pointer transition-all bg-[#D4AF37]/10 ${jobBoardFilter === 'available' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setJobBoardFilter('available')}
            data-testid="filter-available"
          >
            <p className="text-2xl font-bold text-[#D4AF37]">{jobBoard.stats.available || 0}</p>
            <p className="text-xs text-[#D4AF37]">Available</p>
          </div>
          <div 
            className={`card p-3 text-center cursor-pointer transition-all bg-[var(--warning)]/10 ${jobBoardFilter === 'claimed' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => setJobBoardFilter('claimed')}
            data-testid="filter-claimed"
          >
            <p className="text-2xl font-bold text-[var(--warning)]">{jobBoard.stats.claimed || 0}</p>
            <p className="text-xs text-[var(--warning)]">Claimed</p>
          </div>
          <div 
            className={`card p-3 text-center cursor-pointer transition-all bg-[var(--success)]/10 ${jobBoardFilter === 'confirmed' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setJobBoardFilter('confirmed')}
            data-testid="filter-confirmed"
          >
            <p className="text-2xl font-bold text-[var(--success)]">{jobBoard.stats.confirmed || 0}</p>
            <p className="text-xs text-[var(--success)]">Assigned</p>
          </div>
          <div 
            className={`card p-3 text-center cursor-pointer transition-all bg-[#F4C2C2]/10 ${jobBoardFilter === 'in_progress' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => setJobBoardFilter('in_progress')}
            data-testid="filter-in-progress"
          >
            <p className="text-2xl font-bold text-[#F4C2C2]">{jobBoard.stats.in_progress || 0}</p>
            <p className="text-xs text-[#F4C2C2]">In Progress</p>
          </div>
          <div 
            className={`card p-3 text-center cursor-pointer transition-all bg-emerald-50 ${jobBoardFilter === 'completed' ? 'ring-2 ring-emerald-500' : ''}`}
            onClick={() => setJobBoardFilter('completed')}
            data-testid="filter-completed"
          >
            <p className="text-2xl font-bold text-emerald-600">{jobBoard.stats.completed || 0}</p>
            <p className="text-xs text-emerald-500">Completed</p>
          </div>
        </div>
      )}

      {/* Worker Hours Overview - Collapsible */}
      <details className="mb-6">
        <summary className="cursor-pointer text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Worker Weekly Hours ({allWorkersHours.length})
        </summary>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
          {allWorkersHours.map(worker => (
            <div 
              key={worker.worker_id} 
              className={`card p-3 cursor-pointer hover:shadow-lg transition-shadow ${
                worker.at_capacity ? 'border-[var(--error)]/30 bg-[var(--error)]/10' : ''
              }`}
              onClick={() => handleViewWorkerProfile(worker.worker_id)}
              data-testid={`worker-hours-${worker.worker_id}`}
            >
              <p className="font-semibold text-sm truncate">{worker.worker_name}</p>
              <p className="text-xl font-bold">{worker.total_hours.toFixed(1)}<span className="text-xs text-[var(--text-muted)]">/{worker.weekly_cap}h</span></p>
              <div className="w-full bg-[var(--background-subtle)] rounded-full h-1.5 mt-1">
                <div 
                  className={`h-1.5 rounded-full ${worker.at_capacity ? 'bg-red-500' : 'bg-[var(--primary)]'}`}
                  style={{ width: `${Math.min(100, (worker.total_hours / worker.weekly_cap) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </details>

      {/* Job Board Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--background-subtle)]">
              <tr>
                {selectMode && !showWorkerPreview && <th className="w-10 p-4"></th>}
                <th className="text-left p-4 font-semibold">Job</th>
                <th className="text-left p-4 font-semibold">Date/Time</th>
                <th className="text-left p-4 font-semibold">Location</th>
                {!showWorkerPreview && <th className="text-left p-4 font-semibold">Status</th>}
                {!showWorkerPreview && <th className="text-left p-4 font-semibold">Workers</th>}
                <th className="text-left p-4 font-semibold">Pay</th>
                <th className="text-right p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobBoard.jobs
                ?.filter(job => {
                  // In worker preview, only show available jobs
                  if (showWorkerPreview) return job.status === 'available';
                  return jobBoardFilter === 'all' || job.status === jobBoardFilter;
                })
                .map(job => (
                <tr 
                  key={job.id} 
                  className={`border-t hover:bg-[var(--background-subtle)] ${selectMode && selectedJobs.includes(job.id) ? 'bg-[var(--accent)]' : ''}`}
                  data-testid={`job-row-${job.id}`}
                >
                  {selectMode && !showWorkerPreview && (
                    <td className="p-4">
                      {!['in_progress', 'completed'].includes(job.status) && (
                        <button onClick={() => toggleSelectJob(job.id)}>
                          {selectedJobs.includes(job.id) ? (
                            <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      )}
                    </td>
                  )}
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        job.service_type === 'private_concierge' || job.service_type === 'social_engagement' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-[var(--success)]/15 text-green-700'
                      }`}>
                        {job.service_type?.toUpperCase()}
                      </span>
                      <span className="font-medium">{job.service_name || 'Service'}</span>
                      {/* Urgent Badge */}
                      {job.is_urgent && (
                        <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ml-2">
                          <AlertTriangle className="w-3 h-3" />
                          URGENT +25%
                        </span>
                      )}
                    </div>
                    {!showWorkerPreview && job.customer_name && <p className="text-sm text-[var(--text-muted)]">{job.customer_name}</p>}
                  </td>
                  <td className="p-4">
                    <p className="font-medium">{job.scheduled_date}</p>
                    <p className="text-sm text-[var(--text-muted)]">{job.scheduled_time} ({job.duration_hours}h)</p>
                  </td>
                  <td className="p-4">
                    <p className="text-sm">{job.city}, {job.state}</p>
                    {showWorkerPreview && <p className="text-xs text-[var(--text-muted)]">{job.address}</p>}
                  </td>
                  {!showWorkerPreview && (
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        job.status === 'pending' ? 'bg-[var(--background-subtle)] text-[var(--text-primary)]' :
                        job.status === 'available' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                        job.status === 'claimed' ? 'bg-[var(--warning)]/15 text-yellow-700' :
                        job.status === 'confirmed' ? 'bg-[var(--success)]/15 text-green-700' :
                        job.status === 'in_progress' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                        job.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-[var(--background-subtle)] text-[var(--text-primary)]'
                      }`}>
                        {job.status?.replace('_', ' ')}
                      </span>
                    </td>
                  )}
                  {!showWorkerPreview && (
                    <td className="p-4">
                      <div className="space-y-2">
                        {/* Workers Needed - Editable for multi-worker jobs */}
                        {isServiceJob(job) && (
                          <div className="mb-2">
                            {editingWorkersNeeded === job.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setWorkersNeededValue(Math.max(1, workersNeededValue - 1))}
                                  className="p-1 bg-purple-100 hover:bg-purple-200 rounded text-purple-700"
                                  disabled={workersNeededValue <= 1}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-6 text-center font-bold text-purple-700">{workersNeededValue}</span>
                                <button
                                  onClick={() => setWorkersNeededValue(Math.min(10, workersNeededValue + 1))}
                                  className="p-1 bg-purple-100 hover:bg-purple-200 rounded text-purple-700"
                                  disabled={workersNeededValue >= 10}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleUpdateWorkersNeeded(job.id)}
                                  disabled={savingWorkersNeeded}
                                  className="p-1 bg-purple-600 hover:bg-purple-700 rounded text-white ml-1"
                                >
                                  {savingWorkersNeeded ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                </button>
                                <button
                                  onClick={() => setEditingWorkersNeeded(null)}
                                  className="p-1 hover:bg-gray-100 rounded text-gray-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingWorkersNeeded(job.id);
                                  setWorkersNeededValue(job.workers_needed || 1);
                                }}
                                className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full hover:bg-purple-200 transition-colors flex items-center gap-1"
                                title="Click to edit workers needed"
                              >
                                <Users className="w-3 h-3" />
                                {(job.workers?.length || (job.worker_id ? 1 : 0))}/{job.workers_needed || 1} needed
                              </button>
                            )}
                          </div>
                        )}
                        
                        {/* Assigned Workers Display */}
                        {job.workers && job.workers.length > 0 ? (
                          <div>
                            {job.workers.map((w, idx) => (
                              <p key={w.id || idx} className="font-medium text-green-700 text-sm">
                                {w.name} {w.role === 'primary' && '(Lead)'}
                              </p>
                            ))}
                            {job.workers.length > 1 && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                {job.workers.length} workers
                              </span>
                            )}
                          </div>
                        ) : job.worker_details ? (
                          <div>
                            <p className="font-medium text-green-700">{job.worker_details.full_name}</p>
                            <p className="text-xs text-[var(--text-muted)]">{job.worker_details.email}</p>
                          </div>
                        ) : job.claims?.length > 0 ? (
                          <div>
                            <p className="text-yellow-700 font-medium">{job.claims.length} claim(s)</p>
                            {job.claims.slice(0, 2).map(c => (
                              <p key={c.id} className="text-xs text-[var(--text-muted)]">{c.worker_name}</p>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[var(--text-muted)]">No workers</span>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="p-4">
                    {/* Show price only if worker_pay is set, or in owner mode show calculated */}
                    {showWorkerPreview ? (
                      job.worker_pay ? (
                        <div>
                          <p className="font-bold text-[var(--primary)]">${job.worker_pay.toFixed(2)}</p>
                          {job.is_urgent && (
                            <p className="text-xs text-amber-600">+25% urgent pay</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-[var(--text-muted)]">—</p>
                      )
                    ) : (
                      <>
                        <p className="font-bold text-[var(--primary)]">
                          ${(job.worker_pay || job.total_amount || (job.duration_hours * 22))?.toFixed(2)}
                        </p>
                        {job.is_urgent && job.urgent_fee_amount > 0 && (
                          <p className="text-xs text-amber-600">+${job.urgent_fee_amount?.toFixed(2)} urgent</p>
                        )}
                        {!job.worker_pay && (
                          <p className="text-xs text-orange-500">Not set for workers</p>
                        )}
                      </>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      {/* Note Icon */}
                      {!showWorkerPreview && (
                        <NoteIconButton
                          entityType="job"
                          entityId={job.id}
                          entityName={`${job.service_name || job.service_type} - ${job.scheduled_date}`}
                          noteCount={noteCounts[`job_${job.id}`] || 0}
                          onOpen={(type, id, name) => setNotesModal({ open: true, entityType: type, entityId: id, entityName: name })}
                        />
                      )}
                      {/* Worker Preview Mode - Show Claim button */}
                      {showWorkerPreview && job.status === 'available' && (
                        <Button size="sm" className="bg-[#E6C965] hover:bg-blue-700 text-white" disabled>
                          Claim Job
                        </Button>
                      )}
                      {/* Regular Owner Actions */}
                      {!showWorkerPreview && (
                        <>
                          {/* Actions based on status */}
                          {job.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => handlePostJob(job.id)} data-testid={`post-job-${job.id}`}>
                              Post
                            </Button>
                          )}
                          {job.status === 'claimed' && job.claims?.length > 0 && (
                            <>
                              <Button 
                                size="sm" 
                                className="bg-[var(--success)] hover:bg-[var(--success)]/80 text-white"
                                onClick={() => handleApproveJob(job.id, job.claims[0].worker_id, job.claims[0].worker_name)}
                                data-testid={`approve-job-${job.id}`}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDenyJob(job.id, job.claims[0].worker_id)}
                                data-testid={`deny-job-${job.id}`}
                              >
                                Deny
                              </Button>
                            </>
                          )}
                          {(job.status === 'available' || job.status === 'pending') && (
                            <>
                              <Select onValueChange={(workerId) => handleManualAssign(job.id, workerId)}>
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue placeholder="Assign" />
                                </SelectTrigger>
                                <SelectContent>
                                  {workers.filter(w => w.is_verified).map(worker => (
                                    <SelectItem key={worker.id} value={worker.id}>
                                      {worker.full_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {/* Assign to Me button for owner */}
                              <Button 
                                size="sm" 
                                className="bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white"
                                onClick={() => handleAssignToMe(job)}
                                disabled={assigningToMe === job.id}
                                data-testid={`assign-to-me-${job.id}`}
                              >
                                {assigningToMe === job.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <User className="w-4 h-4 mr-1" />
                                    Me
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                          {job.status === 'confirmed' && job.worker_id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                if (window.confirm('Unassign this worker? Job will go back to available.')) {
                                  jobsAPI.unassignJob(job.id, 'Manual unassignment')
                                    .then(() => { toast.success('Worker unassigned'); fetchAllData(); })
                                    .catch(e => toast.error(e.response?.data?.detail || 'Failed'));
                                }
                              }}
                              data-testid={`unassign-job-${job.id}`}
                            >
                              Unassign
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setEditingJob(job);
                              setShowJobEditModal(true);
                            }}
                            data-testid={`edit-job-${job.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {/* Delete button - only for non-completed/in-progress jobs */}
                          {!['in_progress', 'completed'].includes(job.status) && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                              onClick={() => handleDeleteJob(job.id, `${job.service_name || job.service_type} on ${job.scheduled_date}`)}
                              data-testid={`delete-job-${job.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!jobBoard.jobs || jobBoard.jobs.length === 0) && (
            <div className="p-8 text-center text-[var(--text-muted)]">
              No jobs found. Create bookings to see them here.
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default JobBoardTab;
